import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Users, ChevronRight, Zap, Clock, AlertTriangle, UserCircle2, Plus,
} from "lucide-react";
import { fmtKwp } from "../../lib/format";

/** Resolve absolute CAPEX £ using the same priority chain as the backend
 *  (direct override → per-kWp × system size → 850 £/kWp default). */
function resolveCapex(p) {
  const direct = p?.capex_gbp;
  if (direct != null && Number(direct) > 0) return Number(direct);
  const kwp = Number(p?.system_size_kwp || 0);
  if (!kwp) return 0;
  const perKwp = Number(p?.capex_per_kwp_gbp ?? 850);
  return kwp * perKwp;
}
const GBP0 = new Intl.NumberFormat("en-GB", {
  style: "currency", currency: "GBP", maximumFractionDigits: 0,
});
import { classTone, resolveClass, CLASS_BY_KEY } from "../../lib/classification";
import KanbanRiskPill from "./KanbanRiskPill";
import MarqueeSelector from "./MarqueeSelector";
import StageTrendSparkline from "./StageTrendSparkline";

/** Format days as a short human string (h / d / mo). Shared across views. */
export function fmtDays(v) {
  if (v == null) return "—";
  if (v < 1) return `${Math.round(v * 24)}h`;
  if (v < 30) return `${v.toFixed(v < 10 ? 1 : 0)}d`;
  return `${(v / 30).toFixed(1)}mo`;
}


/**
 * Pipeline summary bar — one horizontal reporting strip across all
 * active stages showing project count + kWp capacity per stage.
 */
export function PipelineSummary({ statuses, filteredTotals, filteredByStatus }) {
  return (
    <div
      className="mb-6 border border-[var(--line)] rounded-lg bg-white overflow-x-auto"
      data-testid="pm-pipeline-summary"
    >
      <div className="flex divide-x divide-[var(--line-soft,#EEE)] min-w-max">
        {statuses.map((s) => {
          const count = (filteredByStatus[s.key] || []).length;
          const kwp = filteredTotals[s.key] || 0;
          return (
            <div
              key={s.key}
              className="px-4 py-3 min-w-[150px] flex-1"
              data-testid={`pm-summary-stage-${s.key}`}
            >
              <div className="label !text-[9px] truncate mb-1">{s.label}</div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-xl font-bold leading-none" data-testid={`pm-summary-count-${s.key}`}>
                  {count}
                </span>
                <span className="text-[11px] muted">
                  project{count === 1 ? "" : "s"}
                </span>
              </div>
              <div
                className="text-sm mt-1 font-mono font-semibold flex items-center gap-1.5"
                style={{ color: "var(--accent-hover)" }}
                data-testid={`pm-summary-kwp-${s.key}`}
              >
                <Zap size={13} /> {fmtKwp(kwp)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * Kanban board — horizontal-scroll columns with drag-and-drop, sparkline
 * trend, SLA overrun pill, and stage duration KPI in each column header.
 */
export function KanbanBoard({ data, statusMeta, projectsByStatus, filteredTotals,
                              canAdvance, canAssign, onAdvance, onAssign, onRiskChange,
                              canBulkSelect, selectedIds, onToggleSelected, onMarqueeSelect,
                              dragging, setDragging, onDropStage, onOpenTrend,
                              canCreateAtStage }) {
  const [hoverStage, setHoverStage] = useState(null);
  const durationByStage = {};
  for (const s of data.statuses) {
    durationByStage[s.key] = {
      avg_completed: s.avg_completed_days,
      avg_current: s.avg_current_days,
      completed_sample: s.completed_sample,
      sla_days: s.sla_days,
      overrun_count: s.overrun_count,
    };
  }
  return (
    <MarqueeSelector
      enabled={!!canBulkSelect}
      onSelect={onMarqueeSelect}
      cardSelector="[data-testid^='pm-card-'][data-classification]"
    >
      <div className="overflow-x-auto pb-4" data-testid="pm-kanban">
        <div className="flex gap-3" style={{ minWidth: "max-content" }}>
        {data.statuses.map((s) => {
          const list = projectsByStatus[s.key] || [];
          const kwp = filteredTotals[s.key] || 0;
          const dur = durationByStage[s.key] || {};
          const primary = dur.avg_current ?? dur.avg_completed;
          const primaryLabel = dur.avg_current != null ? "avg in stage" : "avg to advance";
          const isDropTarget = hoverStage === s.key && dragging && dragging.lifecycle_status !== s.key;
          const overrun = dur.overrun_count || 0;
          return (
            <div
              key={s.key}
              className={`w-72 shrink-0 border rounded-lg flex flex-col transition-colors ${
                isDropTarget
                  ? "bg-[var(--accent-soft,#DDF3F1)] border-[var(--accent-hover)] border-2 border-dashed"
                  : "bg-[var(--surface-2,#F6F7F9)] border-[var(--line)]"
              }`}
              data-testid={`pm-col-${s.key}`}
              onDragOver={(e) => {
                if (!dragging) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (hoverStage !== s.key) setHoverStage(s.key);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                if (hoverStage === s.key) setHoverStage(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setHoverStage(null);
                if (dragging) onDropStage(dragging, s.key);
                setDragging(null);
              }}
            >
              <div className="px-3 py-2.5 border-b border-[var(--line)] sticky top-0 bg-inherit rounded-t-lg">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm truncate">{s.label}</div>
                  <span className="text-[11px] muted font-mono shrink-0">{list.length}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <div
                    className="text-sm font-mono font-semibold flex items-center gap-1.5"
                    style={{ color: "var(--accent-hover)" }}
                    data-testid={`pm-col-kwp-${s.key}`}
                  >
                    <Zap size={13} /> {fmtKwp(kwp)}
                  </div>
                  {primary != null && (
                    <div
                      className="text-[10px] muted font-mono inline-flex items-center gap-1"
                      title={
                        dur.avg_current != null
                          ? `Average time projects currently in this stage have been sitting here (${list.length} project${list.length === 1 ? "" : "s"}).`
                          : `Average time projects spent in this stage before advancing (n=${dur.completed_sample || 0}).`
                      }
                      data-testid={`pm-col-duration-${s.key}`}
                    >
                      <Clock size={9} /> {fmtDays(primary)} · {primaryLabel}
                    </div>
                  )}
                </div>
                {(dur.sla_days != null || overrun > 0) && (
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {dur.sla_days != null && (
                      <span
                        className="text-[10px] font-mono muted inline-flex items-center gap-1"
                        data-testid={`pm-col-sla-${s.key}`}
                        title={`Target time in this stage: ${dur.sla_days} days`}
                      >
                        SLA {dur.sla_days}d
                      </span>
                    )}
                    {overrun > 0 && (
                      <span
                        className="text-[10px] font-bold inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                        style={{ color: "#C0362C", background: "#FCE7E5" }}
                        data-testid={`pm-col-overrun-${s.key}`}
                        title={`${overrun} project${overrun === 1 ? "" : "s"} in this stage exceed the SLA`}
                      >
                        <AlertTriangle size={9} /> {overrun} over
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span
                    className="text-[9px] font-mono muted uppercase tracking-wider shrink-0"
                    title="Rolling 8-week trend of average days spent in this stage before advancing"
                  >
                    8w trend
                  </span>
                  <StageTrendSparkline
                    trend={s.weekly_trend}
                    testid={`pm-col-sparkline-${s.key}`}
                    onClick={() => onOpenTrend?.(s)}
                  />
                </div>
              </div>
              <div className="p-2 space-y-2 min-h-[80px]">
                {canCreateAtStage && (
                  <Link
                    to={`/projects/new?stage=${encodeURIComponent(s.key)}`}
                    className="w-full flex items-center justify-center gap-1 text-[11px] muted italic
                               border border-dashed border-[var(--line)] rounded py-1.5 mb-1
                               hover:bg-white hover:muted-none hover:text-[var(--accent-hover)]
                               hover:border-[var(--accent-hover)] transition-colors"
                    data-testid={`pm-col-add-${s.key}`}
                    title={`Create a new project directly in ${s.label}`}
                  >
                    <Plus size={11} /> add here
                  </Link>
                )}
                {list.map((p, idx) => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    statusMeta={statusMeta}
                    canAdvance={canAdvance(p)}
                    canAssign={canAssign(p)}
                    onAdvance={() => onAdvance(p)}
                    onAssign={() => onAssign(p)}
                    onRiskChange={onRiskChange}
                    canBulkSelect={canBulkSelect}
                    isSelected={selectedIds?.has(p.id)}
                    onToggleSelected={(e) => onToggleSelected?.(p, s.key, idx, e.shiftKey)}
                    onDragStart={() => setDragging(p)}
                    onDragEnd={() => setDragging(null)}
                    isDragging={dragging?.id === p.id}
                  />
                ))}
                {!list.length && (
                  <div className="text-[11px] muted text-center py-4 italic">
                    {isDropTarget ? "release to move here" : "no projects"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </MarqueeSelector>
  );
}


/**
 * Denser list view — one collapsible section per stage. No drag-drop.
 */
export function ListView({ data, statusMeta, projectsByStatus, filteredTotals,
                            canAdvance, canAssign, onAdvance, onAssign, onRiskChange }) {
  const [collapsed, setCollapsed] = useState({});
  return (
    <div className="space-y-4" data-testid="pm-list">
      {data.statuses.map((s) => {
        const list = projectsByStatus[s.key] || [];
        const kwp = filteredTotals[s.key] || 0;
        const capex = list.reduce((sum, p) => sum + resolveCapex(p), 0);
        const isCollapsed = !!collapsed[s.key];
        return (
          <div key={s.key} className="border border-[var(--line)] rounded-lg overflow-hidden bg-white">
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [s.key]: !c[s.key] }))}
              className="w-full flex items-center justify-between px-4 py-3 border-b border-[var(--line)] hover:bg-[var(--surface-2,#F6F7F9)] text-left"
              data-testid={`pm-list-header-${s.key}`}
            >
              <div className="flex items-center gap-3">
                <ChevronRight
                  size={14}
                  className="muted transition-transform"
                  style={{ transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)" }}
                />
                <div className="font-semibold text-sm">{s.label}</div>
                <span
                  className="text-sm font-mono font-semibold inline-flex items-center gap-1.5"
                  style={{ color: "var(--accent-hover)" }}
                  data-testid={`pm-list-kwp-${s.key}`}
                >
                  <Zap size={13} /> {fmtKwp(kwp)}
                </span>
                {capex > 0 && (
                  <span
                    className="text-xs font-mono font-semibold px-2 py-0.5 rounded"
                    style={{ background: "#F3F4F6", color: "#374151" }}
                    title="Total resolved CAPEX for this stage (filtered view)"
                    data-testid={`pm-list-capex-${s.key}`}
                  >
                    {GBP0.format(capex)}
                  </span>
                )}
              </div>
              <div className="text-xs muted font-mono">{list.length}</div>
            </button>
            {!isCollapsed && list.length > 0 && (
              <div className="divide-y divide-[var(--line-soft,#EEE)]">
                {list.map((p) => {
                  const pCls = resolveClass(p.business_classification);
                  const pTone = classTone(pCls);
                  const pMeta = CLASS_BY_KEY[pCls];
                  return (
                  <div key={p.id} className="p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          aria-hidden
                          className="inline-block rounded-full shrink-0"
                          style={{ width: 8, height: 8, background: pTone, boxShadow: `0 0 0 1px ${pTone}55` }}
                          title={`${pMeta.label} — ${pMeta.sub}`}
                          data-testid={`pm-list-class-swatch-${p.id}`}
                        />
                        <Link to={`/projects/${p.id}`} className="font-medium hover:underline">{p.name}</Link>
                        <span
                          className="text-sm font-mono font-semibold inline-flex items-center gap-1 shrink-0"
                          style={{ color: "var(--accent-hover)" }}
                          data-testid={`pm-list-item-kwp-${p.id}`}
                        >
                          <Zap size={12} /> {fmtKwp(p.system_size_kwp || 0)}
                        </span>
                        {resolveCapex(p) > 0 && (
                          <span
                            className="text-xs font-mono font-semibold px-1.5 py-0.5 rounded shrink-0"
                            style={{ background: "#F3F4F6", color: "#374151" }}
                            title={
                              (p.capex_gbp != null && Number(p.capex_gbp) > 0)
                                ? "Direct £ override"
                                : `£${Number(p.capex_per_kwp_gbp ?? 850)}/kWp × ${Number(p.system_size_kwp || 0).toFixed(2)} kWp`
                            }
                            data-testid={`pm-list-item-capex-${p.id}`}
                          >
                            {GBP0.format(resolveCapex(p))}
                          </span>
                        )}
                      </div>
                      <div className="text-xs muted truncate">
                        {p.customer_name} · {p.address || "—"} {p.postcode ? `· ${p.postcode}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <KanbanRiskPill
                        projectId={p.id}
                        value={p.risk_status}
                        disabled={!onRiskChange}
                        onChange={(next) => onRiskChange?.(p, next)}
                      />
                      {canAssign(p) && (
                        <button
                          className="btn-ghost text-xs"
                          onClick={() => onAssign(p)}
                          data-testid={`pm-list-assign-${p.id}`}
                        >
                          <Users size={12} /> Assignees
                        </button>
                      )}
                      {canAdvance(p) && statusMeta[p.lifecycle_status || "solar_request"]?.next && (
                        <button
                          className="btn-accent text-xs"
                          onClick={() => onAdvance(p)}
                          data-testid={`pm-list-advance-${p.id}`}
                        >
                          Advance <ArrowRight size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
            {!isCollapsed && !list.length && (
              <div className="px-4 py-6 text-xs muted italic text-center">no projects in this stage</div>
            )}
          </div>
        );
      })}
    </div>
  );
}


/**
 * Kanban card. Shows project name, customer, kWp, Asset Manager, days
 * in stage vs SLA, and action buttons (Assignees / Advance). Draggable
 * when the caller has advance permission.
 */
export function ProjectCard({ project, statusMeta, canAdvance, canAssign, onAdvance, onAssign,
                               onRiskChange,
                               canBulkSelect, isSelected, onToggleSelected,
                               onDragStart, onDragEnd, isDragging }) {
  const cur = project.lifecycle_status || "solar_request";
  const next = statusMeta[cur]?.next;
  const kwp = Number(project.system_size_kwp) || 0;
  const amName = project.asset_manager_name;
  const overrun = project.sla_overrun;
  const stageDays = project.days_in_current_stage;
  const sla = project.stage_sla_days;
  const draggable = canAdvance;
  const clsKey = resolveClass(project.business_classification);
  const clsMeta = CLASS_BY_KEY[clsKey];
  const clsTone = classTone(clsKey);
  // Legacy status vs lifecycle mismatch — a project marked `completed`
  // (or `monitoring`) via the legacy `status` field but still stuck at
  // an earlier lifecycle_status. Ops needs to see this so the two views
  // (PM board vs Investor Dashboard) don't stay out of sync. Highlighted
  // with an amber ring and a small "STATUS" pill.
  const legacyDone = ["completed", "monitoring"].includes(
    (project.status || "").toLowerCase(),
  );
  const lifecycleMismatch = legacyDone && cur !== "final_handover";
  return (
    <div
      className={`bg-white border rounded p-2.5 shadow-sm transition-opacity ${
        overrun ? "border-l-[3px] border-l-[#C0362C]"
                : lifecycleMismatch ? "border-l-[3px] border-l-[#B8721E]"
                : "border-[var(--line)]"
      } ${isSelected ? "ring-2 ring-[var(--accent-hover)] ring-offset-1" : ""} ${isDragging ? "opacity-40" : ""} ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
      data-testid={`pm-card-${project.id}`}
      data-classification={clsKey}
      data-lifecycle-mismatch={lifecycleMismatch ? "true" : "false"}
      data-selected={isSelected ? "true" : "false"}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", project.id);
        onDragStart?.();
      }}
      onDragEnd={() => onDragEnd?.()}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          {canBulkSelect && (
            <input
              type="checkbox"
              checked={!!isSelected}
              onClick={(e) => {
                // Stop bubbling so the card doesn't start a drag and the
                // parent Link doesn't navigate on the wrapper's click.
                e.stopPropagation();
                onToggleSelected?.(e);
              }}
              onChange={() => {}}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
              className="shrink-0 cursor-pointer"
              title="Shift-click to select a range"
              aria-label={`Select ${project.name} for bulk edit`}
              data-testid={`pm-card-select-${project.id}`}
            />
          )}
          <span
            aria-hidden
            className="inline-block rounded-full shrink-0"
            style={{
              width: 8,
              height: 8,
              background: clsTone,
              boxShadow: `0 0 0 1px ${clsTone}55`,
            }}
            title={`${clsMeta.label} — ${clsMeta.sub}`}
            data-testid={`pm-card-class-swatch-${project.id}`}
          />
          <Link
            to={`/projects/${project.id}`}
            className="font-semibold text-sm leading-tight hover:underline min-w-0 flex-1"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
            }}
            title={project.name}
            data-testid={`pm-card-name-${project.id}`}
          >
            {project.name}
          </Link>
        </div>
        {lifecycleMismatch && (
          <span
            className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0"
            style={{ color: "#8A5410", background: "#FCE9CE" }}
            data-testid={`pm-card-lifecycle-mismatch-${project.id}`}
            title={`Marked "${project.status}" in the legacy status field but lifecycle is still "${cur}". Ask an admin to run the lifecycle sanity fix.`}
          >
            <AlertTriangle size={9} /> STATUS
          </span>
        )}
        <KanbanRiskPill
          projectId={project.id}
          value={project.risk_status}
          disabled={!onRiskChange}
          onChange={(next) => onRiskChange?.(project, next)}
        />
        {overrun && (
          <span
            className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0"
            style={{ color: "#C0362C", background: "#FCE7E5" }}
            data-testid={`pm-card-overrun-${project.id}`}
            title={sla != null ? `In this stage for ${stageDays}d — SLA is ${sla}d.` : "Past stage SLA"}
          >
            <AlertTriangle size={9} /> SLA
          </span>
        )}
      </div>
      <div className="text-[11px] muted mb-1 truncate">{project.customer_name}</div>
      {(project.unit_code || project.property_code) && (
        <div
          className="text-[10px] font-mono muted mb-1 truncate flex items-center gap-1.5"
          title={[
            project.unit_code ? `Unit: ${project.unit_code}` : null,
            project.property_code ? `Property: ${project.property_code}` : null,
          ].filter(Boolean).join(" · ")}
          data-testid={`pm-card-codes-${project.id}`}
        >
          {project.unit_code && (
            <span data-testid={`pm-card-unit-${project.id}`}>U · {project.unit_code}</span>
          )}
          {project.unit_code && project.property_code && (
            <span className="opacity-40">·</span>
          )}
          {project.property_code && (
            <span data-testid={`pm-card-property-${project.id}`}>P · {project.property_code}</span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-[10px] muted truncate flex-1">
          {project.postcode || project.address || ""}
        </div>
        <div
          className="text-sm font-mono font-semibold inline-flex items-center gap-1 shrink-0"
          style={{ color: kwp > 0 ? "var(--accent-hover)" : "var(--ink-3, #999)" }}
          data-testid={`pm-card-kwp-${project.id}`}
          title={kwp > 0 ? "Primary design capacity" : "No design captured yet"}
        >
          <Zap size={13} /> {kwp > 0 ? fmtKwp(kwp) : "—"}
        </div>
      </div>
      <div
        className="text-[10px] mb-2 inline-flex items-center gap-1 truncate max-w-full"
        data-testid={`pm-card-am-${project.id}`}
        title={amName ? `Asset Manager: ${amName}` : "No Asset Manager assigned"}
      >
        <UserCircle2 size={10} className="shrink-0" style={{ color: amName ? "var(--accent-hover)" : "var(--ink-3, #999)" }} />
        <span className={amName ? "" : "muted italic"}>
          {amName ? amName : "AM: unassigned"}
        </span>
      </div>
      {stageDays != null && (
        <div
          className="text-[10px] font-mono muted mb-2 inline-flex items-center gap-1"
          data-testid={`pm-card-stage-days-${project.id}`}
        >
          <Clock size={9} />
          <span style={{ color: overrun ? "#C0362C" : "inherit", fontWeight: overrun ? 600 : 400 }}>
            {fmtDays(stageDays)} in stage
          </span>
          {sla != null && <span className="muted">· SLA {sla}d</span>}
        </div>
      )}
      {cur === "not_proceeding" && project.not_proceeding_reason && (
        <div
          className="text-[10px] mb-2 px-2 py-1 rounded"
          style={{ background: "#F4F4F5", color: "#3F3F46", borderLeft: "2px solid #71717A" }}
          data-testid={`pm-card-np-reason-${project.id}`}
          title={project.not_proceeding_reason}
        >
          <span className="font-semibold uppercase tracking-wide text-[9px] mr-1">Reason:</span>
          {project.not_proceeding_reason}
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        {canAssign && (
          <button
            className="btn-ghost !py-1 !px-2 text-[11px]"
            onClick={onAssign}
            title="Assign PM roles"
            data-testid={`pm-card-assign-${project.id}`}
          >
            <Users size={11} />
          </button>
        )}
        {canAdvance && next && (
          <button
            className="btn-accent !py-1 !px-2 text-[11px] ml-auto"
            onClick={onAdvance}
            data-testid={`pm-card-advance-${project.id}`}
          >
            Advance <ArrowRight size={11} />
          </button>
        )}
        {!canAdvance && next && (
          <span className="text-[10px] muted ml-auto italic" title="Awaiting the assigned approver">
            awaiting approver
          </span>
        )}
        {!next && (
          <span className="text-[10px] ml-auto" style={{ color: "var(--accent-hover)" }}>
            final stage ✓
          </span>
        )}
      </div>
    </div>
  );
}
